import type { StateObserver } from '../../../lib/pattern/StateMachine';
import { arrayIterate2 } from '../../../lib/util/jsTools';
import { Container } from '../../../odie';
import { Cell } from '../../components/MapComponent';
import { setAlreadyShuffled } from '../../util/matchTools';
import type { IPhase, PhaseSystem } from '../PhaseSystem';

/*
    handles game session player input phase
*/
export class InputPhase implements StateObserver, IPhase {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _system: PhaseSystem;
  public disabled = false;
  public interceptTap?: (cell: Cell) => void;

  // init
  //-------------------------------------------------------------------------
  constructor(system: PhaseSystem) {
    this._system = system;
  }

  public tapCount = 0;

  // impl
  //-------------------------------------------------------------------------
  public async enter() {
    const scene = this._system.scene;
    if (!scene) return;

    const phase = scene.sessionEntity.c.phase;

    //TODO: this is a bit wierd. probably better to input monitor the entire map and map that position to the cell.
    // register input with tiles
    arrayIterate2(scene.sessionEntity.c.map.grid, (cell) => {
      if (!cell.tile) return;
      const view = cell.tile.c.view2d.view as Container;
      view.setOnTap(async () => {
        // Only accept input if the game is not over
        if (scene.gameOver) return;
        this._onTap(cell as Cell);
      });
    });

    // Await any pending activations (if any) before triggering a new round. This should allow some blocks
    // like the spider to fully process before the next round starts.
    await phase.waitActivationsComplete();

    // trigger round
    phase.triggerRound();
  }

  public async leave() {
    if (!this._system.scene) return;
    // unregister input with tiles
    arrayIterate2(this._system.scene.sessionEntity.c.map.grid, (cell) => {
      if (!cell.tile) return;
      const view = cell.tile.c.view2d.view as Container;
      view.setOnTap(undefined);
    });
  }

  public step() {
    if (!this._system.scene) return;
    // if activations exist enter active phase
    if (this._system.scene.sessionEntity.c.phase.activations > 0) {
      this._system.phase = 'active';
    }
  }

  // private: input
  //-------------------------------------------------------------------------
  private _onTap(cell: Cell) {
    if (!cell.tile || this.disabled || !this._system.scene) return;

    // Skip input if game is out of moves
    if (this._system.scene.moves <= 0) return;

    // Prevent input while there are remaining activations
    if (this._system.scene.sessionEntity.c.phase.activations > 0) {
      return;
    }

    const pos = cell.tile.c.position.mapPosition;

    // Intercept tap if defined, user for dev cheats
    if (this.interceptTap) {
      this.interceptTap(cell);
      this.interceptTap = undefined;
      return;
    }

    if (this._system.scene.selectedBooster) {
      this._system.scene.runBooster(this._system.scene.selectedBooster, pos);
      return;
    }

    // Increase internal tap count to be sent with the input event
    this.tapCount += 1;

    // Allow shuffle to happen again.
    setAlreadyShuffled(false);

    // notify event, allow input if not handled
    if (
      !this._system.scene.events.publish({
        id: 'input',
        cell,
        tapCount: this.tapCount,
      })
    ) {
      // get overlay else block layer
      const layer = cell.overlay || cell.base;

      // tap collide entity
      layer?.entity.c.block.collide('tap', { type: 'tap', cell });
    }
  }
}
